import { useEffect, useState } from 'react';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { X, Loader2, Copy, Check } from 'lucide-react';
import { toast } from 'sonner';

function eur(n) {
  return Number(n || 0).toLocaleString('fi-FI', { style: 'currency', currency: 'EUR' });
}

function kgFmt(n) {
  return Number(n || 0).toLocaleString('fi-FI', { maximumFractionDigits: 2 }) + ' kg';
}

function dateFi(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString('fi-FI');
}

/**
 * FinvoicePreviewModal — näyttää mitä Finvoice-XML sisältää ennen kuin
 * lasku viedään Suonentietoon. Hakee `/api/invoices/{id}/finvoice-preview`
 * -endpointin, joka palauttaa jäsennelly rivit + raw XML:n. Endpoint EI
 * merkitse laskua viedyksi.
 */
export default function FinvoicePreviewModal({ invoiceId, onClose }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [showXml, setShowXml] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await api.get(`/invoices/${invoiceId}/finvoice-preview`);
        if (alive) setData(res.data);
      } catch (e) {
        if (alive) setError(e.response?.data?.detail || e.message || 'Esikatselun lataus epäonnistui');
      }
    })();
    return () => { alive = false; };
  }, [invoiceId]);

  const copyXml = async () => {
    if (!data?.xml) return;
    try {
      await navigator.clipboard.writeText(data.xml);
      setCopied(true);
      toast.success('XML kopioitu leikepöydälle');
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error('Kopiointi epäonnistui');
    }
  };

  const vatPct = data ? (data.vat_rate * 100).toLocaleString('fi-FI', { maximumFractionDigits: 2 }) : '';

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      role="dialog"
      aria-modal="true"
      data-testid="finvoice-preview-modal"
    >
      <div className="bg-white rounded-lg shadow-xl max-w-5xl w-full max-h-[92vh] overflow-hidden flex flex-col">
        <div className="flex items-center justify-between px-5 py-3 border-b border-[#E4E7DF] shrink-0">
          <div>
            <h2 className="text-lg font-medium text-[#1F2A24]">Finvoice-esikatselu</h2>
            {data && (
              <p className="text-xs text-[#6A6D66] mt-0.5">
                Lasku {data.invoice_no} · {data.customer_name}
                {data.exported_at && (
                  <span className="ml-2 inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] bg-[#E8F0E9] text-[#2A4B30]">
                    Viety {dateFi(data.exported_at)}
                  </span>
                )}
              </p>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            data-testid="finvoice-preview-close"
            className="w-8 h-8 rounded-full hover:bg-[#F4F5F0] text-[#6A6D66] hover:text-[#1F2A24] flex items-center justify-center transition-colors"
            aria-label="Sulje"
          >
            <X size={18} />
          </button>
        </div>

        <div className="overflow-y-auto flex-1 p-5">
          {error && (
            <div className="text-sm text-[#C05A3C] bg-[#FBEAE5] rounded px-3 py-2" data-testid="finvoice-preview-error">
              {error}
            </div>
          )}
          {!data && !error && (
            <div className="flex items-center gap-2 text-sm text-[#6A6D66]">
              <Loader2 size={16} className="animate-spin" /> Ladataan esikatselua…
            </div>
          )}
          {data && !showXml && (
            <div className="space-y-4" data-testid="finvoice-preview-parsed">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                <Metric label="Laskun pvm" value={dateFi(data.invoice_date)} />
                <Metric label="Eräpvm" value={dateFi(data.due_date)} />
                <Metric label="ALV-%" value={`${vatPct} %`} />
                <Metric label="Rivejä" value={String(data.rows?.length ?? 0)} />
              </div>

              <div className="overflow-x-auto border border-[#E4E7DF] rounded">
                <table className="w-full text-sm">
                  <thead className="bg-[#F4F5F0] text-[#3A4038]">
                    <tr>
                      <Th>Tuote</Th>
                      <Th>Toim.pvm</Th>
                      <Th className="text-right">Määrä</Th>
                      <Th className="text-right">À-hinta (brutto)</Th>
                      <Th className="text-right">Veroton à-hinta</Th>
                      <Th className="text-right">Veroton</Th>
                      <Th className="text-right">ALV</Th>
                      <Th className="text-right">Verollinen</Th>
                    </tr>
                  </thead>
                  <tbody data-testid="finvoice-preview-rows">
                    {data.rows.map((r, idx) => (
                      <tr key={idx} className="border-t border-[#E4E7DF]">
                        <Td>
                          <div>{r.product}</div>
                          {(r.lahete_no || r.ostotilaus) && (
                            <div className="text-[10px] text-[#6A6D66] mt-0.5">
                              {r.lahete_no && <>Lähete {r.lahete_no}</>}
                              {r.lahete_no && r.ostotilaus && ' · '}
                              {r.ostotilaus && <>OT {r.ostotilaus}</>}
                            </div>
                          )}
                        </Td>
                        <Td>{dateFi(r.date_iso)}</Td>
                        <Td className="text-right tabular-nums">{kgFmt(r.qty_kg)}</Td>
                        <Td className="text-right tabular-nums">{eur(r.unit_price_gross)}</Td>
                        <Td className="text-right tabular-nums font-medium">{eur(r.unit_price_net)}</Td>
                        <Td className="text-right tabular-nums">{eur(r.line_net)}</Td>
                        <Td className="text-right tabular-nums">{eur(r.line_vat)}</Td>
                        <Td className="text-right tabular-nums font-medium">{eur(r.line_gross)}</Td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot className="bg-[#F4F5F0] border-t-2 border-[#3A4038]">
                    <tr>
                      <Td colSpan={5} className="font-medium">Yhteensä</Td>
                      <Td className="text-right tabular-nums font-medium" data-testid="finvoice-preview-total-net">{eur(data.total_net)}</Td>
                      <Td className="text-right tabular-nums font-medium" data-testid="finvoice-preview-total-vat">{eur(data.total_vat)}</Td>
                      <Td className="text-right tabular-nums font-medium" data-testid="finvoice-preview-total-gross">{eur(data.total_gross)}</Td>
                    </tr>
                  </tfoot>
                </table>
              </div>

              <p className="text-xs text-[#6A6D66]">
                Näytetyt luvut vastaavat Finvoice-XML:n <code>InvoiceRow</code>- ja
                <code> InvoiceTotal</code>-kenttiä. Veroton à-hinta lähetetään <code>UnitPriceAmount</code>-kenttänä
                (VAT EXCLUDED, Finvoice 3.0). Tämä esikatselu ei merkitse laskua viedyksi.
              </p>
            </div>
          )}

          {data && showXml && (
            <div className="space-y-2" data-testid="finvoice-preview-xml">
              <pre className="bg-[#1F2A24] text-[#E4E7DF] text-xs p-3 rounded overflow-auto max-h-[62vh] leading-relaxed">
                {data.xml}
              </pre>
            </div>
          )}
        </div>

        {data && (
          <div className="flex items-center justify-between gap-2 px-5 py-3 border-t border-[#E4E7DF] shrink-0">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowXml((s) => !s)}
              data-testid="finvoice-preview-toggle-xml"
            >
              {showXml ? 'Näytä jäsennelty' : 'Näytä raw XML'}
            </Button>
            <div className="flex items-center gap-2">
              {showXml && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={copyXml}
                  data-testid="finvoice-preview-copy-xml"
                >
                  {copied ? <><Check size={14} className="mr-1" /> Kopioitu</> : <><Copy size={14} className="mr-1" /> Kopioi XML</>}
                </Button>
              )}
              <Button size="sm" onClick={onClose} data-testid="finvoice-preview-close-footer">Sulje</Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function Metric({ label, value }) {
  return (
    <div className="bg-[#F4F5F0] rounded px-3 py-2">
      <div className="text-[10px] uppercase tracking-wide text-[#6A6D66]">{label}</div>
      <div className="text-sm text-[#1F2A24] mt-0.5">{value}</div>
    </div>
  );
}

function Th({ children, className = '' }) {
  return <th className={`text-left px-2 py-1.5 text-[11px] font-medium ${className}`}>{children}</th>;
}

function Td({ children, className = '', ...rest }) {
  return <td className={`px-2 py-1.5 text-[13px] align-top ${className}`} {...rest}>{children}</td>;
}
